import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chip, Popover } from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import EmptyRender from '@/enterprise/components/EmptyRender';
import {
  buildNewDivisionAlertNavigationState,
  useGetNewDivisionAlertData,
} from '@/enterprise/components/NewDivisionAlert';
import { TakerMarketWithRate } from '@/enterprise/components/OfferBuilder/rateHelpers';
import { calculateDiscount, useGetLocationFromPathname } from '@/enterprise/components/OfferBuilder/utils';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useFixedRateDetails from '@/enterprise/features/fixedRate/useFixedRateDetails';
import usePreferredRateDetails from '@/enterprise/features/preferred/usePreferredRateDetails';
import { useOfferDiscountAPRDisplay } from '@/enterprise/features/takerMarketTable/utils/useOfferDiscountAPRDisplay';
import VariableRateDetailModal from '@/enterprise/features/variableRate/DetailModal';
import useVariableRateDetails from '@/enterprise/features/variableRate/useVariableRateDetails';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';

const FixedRateDisplay = ({ showInfo = true, takerMarket }: { showInfo?: boolean; takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const { getFixedRateDetails } = useFixedRateDetails();
  const { formattedMarketRate } = getFixedRateDetails(takerMarket);

  return (
    <div className="flex items-center gap-1">
      {formattedMarketRate}
      {showInfo && (
        <Popover>
          <Popover.Trigger aria-label="Market Rate Info" onClick={(e) => e.stopPropagation()}>
            <InfoCircleIcon className="h-4 w-4 fill-text-secondary" />
          </Popover.Trigger>
          <Popover.Content arrow className="w-40 text-center text-sm" side="bottom">
            {t('offer.fixedRateInfo')}
          </Popover.Content>
        </Popover>
      )}
    </div>
  );
};

const VariableRateDisplay = ({ showInfo = true, takerMarket }: { showInfo?: boolean; takerMarket: TakerMarket }) => {
  const [openRateModal, setOpenRateModal] = useState<boolean>(false);
  const { getVariableRateDetails } = useVariableRateDetails();
  const { formattedDiscountAmount, formattedEstimatedRate, formattedReferenceRate, formattedSpread, rateName } =
    getVariableRateDetails(takerMarket);

  return (
    <>
      <VariableRateDetailModal
        formattedDiscountAmount={formattedDiscountAmount}
        formattedEstimatedRate={formattedEstimatedRate}
        formattedReferenceRate={formattedReferenceRate}
        formattedSpread={formattedSpread}
        onCancel={() => setOpenRateModal(false)}
        open={openRateModal}
        rateName={rateName}
      />
      <div className="flex items-center gap-1">
        {formattedEstimatedRate}
        {showInfo && (
          <button aria-label="Market Rate Info" onClick={() => setOpenRateModal(true)} type="button">
            <InfoCircleIcon className="h-4 w-4 fill-text-secondary" />
          </button>
        )}
      </div>
    </>
  );
};

const PreferredRateDisplay = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const [openRateModal, setOpenRateModal] = useState<boolean>(false);
  const { getPreferredRateDetails } = usePreferredRateDetails();
  const {
    formattedDiscountAmount,
    formattedEstimatedRate,
    formattedReferenceRate,
    formattedSpread,
    rateLabel,
    rateName,
  } = getPreferredRateDetails(takerMarket);
  const displayAsReferenceRate = rateLabel !== undefined;

  return (
    <div>
      {displayAsReferenceRate ? (
        <EmptyRender condition={!!rateName}>
          <VariableRateDetailModal
            formattedDiscountAmount={formattedDiscountAmount}
            formattedEstimatedRate={formattedEstimatedRate}
            formattedReferenceRate={formattedReferenceRate}
            formattedSpread={formattedSpread}
            onCancel={() => setOpenRateModal(false)}
            open={openRateModal}
            rateName={rateName}
            showZeroApAlert={(takerMarket.eligibleInvoiceAmount || 0) <= 0}
          />
          <div className="flex items-center gap-1">
            {formattedEstimatedRate}
            <button aria-label="Market Rate Info" onClick={() => setOpenRateModal(true)} type="button">
              <InfoCircleIcon className="h-4 w-4 fill-text-secondary" />
            </button>
          </div>
        </EmptyRender>
      ) : (
        <EmptyRender condition={!!formattedEstimatedRate}>
          <div className="uppercase">{formattedEstimatedRate}</div>
        </EmptyRender>
      )}
    </div>
  );
};

const NameYourRateDisplay = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();

  const rateDisplay = takerMarket.offerConfig.isDiscountBidding
    ? offerDiscountAPRDisplay(takerMarket.offerConfig.maxDiscount ?? 0, t('core.discountAbbreviation'))
    : offerDiscountAPRDisplay(takerMarket.offerConfig.maxApr ?? 0, t('core.aprAbbreviation'));

  return <div className="uppercase">{rateDisplay}</div>;
};

const NewDivisionRateDisplay = ({
  takerMarketWithRate,
  showDiscountAmount,
}: {
  takerMarketWithRate: TakerMarketWithRate;
  showDiscountAmount: boolean;
}) => {
  const { t } = useTranslation();
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();
  const { asCurrency } = useLocaleFormat();
  const { rate = 0, rateType = 'APR', takerMarket } = takerMarketWithRate;
  const location = useGetLocationFromPathname();

  const rateDisplay =
    rateType === 'DISC'
      ? offerDiscountAPRDisplay(rate, t('core.discountAbbreviation'))
      : offerDiscountAPRDisplay(rate, t('core.aprAbbreviation'));

  // Calculate discount amount
  const discountAmount = calculateDiscount({
    dpeWeightedAvg: takerMarket.eligibleDpeWeightedAvg,
    invoiceAmount: takerMarket.eligibleInvoiceAmount,
    isDiscount: rateType === 'DISC',
    rate: rate ?? 0,
  });

  const navigationState = buildNewDivisionAlertNavigationState(
    [takerMarketWithRate],
    rateType,
    location,
    'division-rate-action'
  );

  return (
    <div className="flex flex-col items-end gap-1">
      <Chip label={t('core.newDivision')} type="info" size="xs" />
      <Link
        className="font-semibold uppercase text-blue-500"
        state={navigationState}
        to={asEnterpriseLink('build-offer/review')}
        title={rateDisplay}
      >
        {rateDisplay}
      </Link>
      {showDiscountAmount && (
        <div className="text-text-secondary">-{asCurrency(discountAmount, takerMarket.currency)}</div>
      )}
    </div>
  );
};

const RateDisplay = ({
  showInfo = true,
  showDiscountAmount = true,
  takerMarket,
}: {
  showInfo?: boolean;
  showDiscountAmount?: boolean;
  takerMarket: TakerMarket;
}) => {
  const { asCurrency } = useLocaleFormat();
  const { takerMarketsWithRate } = useGetNewDivisionAlertData([takerMarket]);
  const { getVariableRateDetails } = useVariableRateDetails();

  const earn = takerMarket.clearingEarn;

  const discountAmountDisplay = showDiscountAmount ? (
    <div className="text-text-secondary">{`${earn > 0 ? '-' : ''}${asCurrency(earn, takerMarket.currency)}`}</div>
  ) : null;

  if (takerMarketsWithRate.length > 0) {
    return (
      <NewDivisionRateDisplay takerMarketWithRate={takerMarketsWithRate[0]} showDiscountAmount={showDiscountAmount} />
    );
  }

  if (takerMarket.offerConfig.offerType === 'PREFERRED_TERM') {
    return (
      <>
        <PreferredRateDisplay takerMarket={takerMarket} />
        {discountAmountDisplay}
      </>
    );
  }

  switch (takerMarket.marketPricingType) {
    case 'PRICE_DISCOVERY':
      return (
        <EmptyRender
          condition={
            (takerMarket.offerConfig.maxApr !== null && takerMarket.offerConfig.maxApr !== undefined) ||
            (takerMarket.offerConfig.maxDiscount !== null && takerMarket.offerConfig.maxDiscount !== undefined)
          }
        >
          <NameYourRateDisplay takerMarket={takerMarket} />
          {discountAmountDisplay}
        </EmptyRender>
      );
    case 'BENCHMARK':
      const variableRateInfo = getVariableRateDetails(takerMarket);
      return (
        <EmptyRender condition={!!variableRateInfo?.rateName}>
          <VariableRateDisplay showInfo={showInfo} takerMarket={takerMarket} />
          {discountAmountDisplay}
        </EmptyRender>
      );
    case 'STATIC':
      return (
        <EmptyRender condition={!!takerMarket.staticMarketRate}>
          <FixedRateDisplay showInfo={showInfo} takerMarket={takerMarket} />
          {discountAmountDisplay}
        </EmptyRender>
      );
    default:
      return <EmptyRender />;
  }
};

export default RateDisplay;
